import { HttpBackend, HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { AppService } from "../../../core/services/app.service";
import {
  fromStripeSmallestUnit,
  toStripeSmallestUnit,
} from "../utils/stripe-price.util";

export interface PaymentSetting {
  id?: number;
  digitalMemoryLogId: number;
  monthlyPrice?: number;
  annualPrice?: number;
  currency?: string;
  active?: boolean;
}

@Injectable({
  providedIn: "root",
})
export class PaymentSettingService {
  private apiUrl = AppService.getApiUrl() + "/api/PaymentSettings";
  private rawHttp: HttpClient;

  constructor(private http: HttpClient, httpBackend: HttpBackend) {
    this.rawHttp = new HttpClient(httpBackend);
  }

  /**
   * GET /api/PaymentSettings/digitalMemoryLog/{digitalMemoryLogId}
   * Used when preview/DML payload has no nested paymentSetting pricing.
   * Pass requireAuth=false on public preview/subscription pages.
   */
  getByDigitalMemoryLogId(
    digitalMemoryLogId: number,
    requireAuth = true
  ): Observable<PaymentSetting | null> {
    const id = Number(digitalMemoryLogId);
    const url = this.apiUrl + "/digitalMemoryLog/" + id;
    // 404 is a valid empty state (new/draft DML has no PaymentSetting yet).
    const options = { params: { _preventToast: "true" } };
    const request = requireAuth ? this.http.get(url, options) : this.rawHttp.get(url);

    return request.pipe(
      map((res: any) => {
        if (res == null) {
          return null;
        }

        if (res && res.IsSuccess === false) {
          return null;
        }

        // Endpoint may return a single setting or a list wrapper.
        const list = this.unwrapList(res);
        if (list.length > 0) {
          return list[0];
        }

        const item = this.unwrapItem(res);
        if (
          item &&
          (item.id != null || item.digitalMemoryLogId != null || item.monthlyPrice != null)
        ) {
          return item;
        }

        return null;
      }),
      catchError((err: any) => {
        if (err && err.status === 404) {
          return of(null);
        }
        throw err;
      })
    );
  }

  create(payload: PaymentSetting): Observable<PaymentSetting> {
    return this.http.post(this.apiUrl, payload).pipe(map((res: any) => this.unwrapItem(res)));
  }

  update(id: number, payload: PaymentSetting): Observable<PaymentSetting> {
    return this.http.put(this.apiUrl + "/" + id, payload).pipe(map((res: any) => this.unwrapItem(res)));
  }

  patch(id: number, payload: Partial<PaymentSetting>): Observable<PaymentSetting> {
    return this.http.patch(this.apiUrl + "/" + id, payload).pipe(map((res: any) => this.unwrapItem(res)));
  }

  delete(id: number): Observable<any> {
    return this.http.delete(this.apiUrl + "/" + id);
  }

  /**
   * Convert display major units to Stripe unit_amount.
   * USD 9.99 → 999; zero-decimal (VND/JPY/…) 12000 → 12000 (no ×100).
   */
  toSmallestUnit(amount: number | string, currency?: string): number {
    return toStripeSmallestUnit(amount, currency);
  }

  /** Convert Stripe unit_amount back to display major units. */
  fromSmallestUnit(
    amount: number | string | null | undefined,
    currency?: string
  ): number | null {
    return fromStripeSmallestUnit(amount, currency);
  }

  private unwrapList(res: any): PaymentSetting[] {
    if (!res) {
      return [];
    }
    if (Array.isArray(res)) {
      return res;
    }
    if (Array.isArray(res.Data)) {
      return res.Data;
    }
    if (res.Data && Array.isArray(res.Data.items)) {
      return res.Data.items;
    }
    return [];
  }

  private unwrapItem(res: any): PaymentSetting {
    if (!res) {
      return res;
    }
    if (res.Data && typeof res.Data === "object" && !Array.isArray(res.Data)) {
      return res.Data;
    }
    return res;
  }
}
